/*
 * The drill-down animation machinery shared by the card-stack menus. It recreates the
 * Ember menus' animation (see frontend/app/components/planner/menus/lesson-menu.hbs and
 * unit-menu.hbs): header and body animate as separate surfaces over 400ms — the header
 * slides vertically (ember-animated's toUp/toDown move-over) while the body slides
 * horizontally (toLeft/toRight) and the popover height glides along (AnimatedContainer).
 * Old and new content travel together like a carousel — adjacent, never overlapping.
 */

import { cx } from "@shared/components/untitledui/util/cx"
import type { Transition } from "motion/react"
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "motion/react"
import type { Key, ReactNode } from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { setHoverBelowPointer } from "./views/hover-below-pointer"

const SWAP_DURATION_S = 0.4

/** ember-animated's default easing (easings/cosine.ts), for the same feel. */
const easeInAndOut = (t: number) => 0.5 - Math.cos(t * Math.PI) / 2

/**
 * Slide-over swap between two surfaces along one axis (ember-animated's move-over):
 * drilling in (`forward`), the new surface pushes the old toward the axis' negative end
 * (body: leftward; header: upward); going back reverses.
 */
export function SlideSwap({
  axis,
  activeKey,
  root,
  surfaceRef,
  isIdle = false,
  onAnimationComplete,
  children,
}: {
  axis: "x" | "y"
  activeKey: Key | null
  root: ReactNode
  /**
   * Optional ref to the wrapper holding both surfaces (BodySwap measures it). It has to sit
   * on this plain div rather than on whichever surface is currently in flow: `motion.div`
   * keeps its external ref in a ref container and memoizes the composed ref on its visual
   * element, so moving a ref prop from one motion surface to another never calls it. The
   * wrapper's height is the in-flow height anyway — the surface that isn't in flow is
   * absolutely positioned (the root by class, the exiting panel by popLayout).
   */
  surfaceRef?: (el: HTMLDivElement | null) => void
  /**
   * True once the swap has landed and nothing is sliding. BodySwap owns that window (it
   * already needs it to lock scrolling) and passes it in; the header swap has no scroll box,
   * so it never asks.
   */
  isIdle?: boolean
  onAnimationComplete?: (key: Key | null) => void
  children: ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const transition: Transition = {
    duration: reduceMotion ? 0 : SWAP_DURATION_S,
    ease: easeInAndOut,
  }
  const isDrilledIn = activeKey !== null
  // The view stops at a new position, but the pointer did not move. The browser applies no
  // hover state to the card that is now below the pointer (see views/hover-below-pointer).
  // The second call is for the next frame, because the popover can move again when it
  // adjusts to its final height.
  const settle = (key: Key | null) => {
    setHoverBelowPointer()
    requestAnimationFrame(setHoverBelowPointer)
    onAnimationComplete?.(key)
  }
  // Animate the `transform` string (not x/y) so Motion hands the slide to WAAPI: it runs
  // on the compositor thread and stays smooth while the main thread is busy mounting the
  // incoming panel.
  const translate = axis === "x" ? "translateX" : "translateY"

  // An idle swap shows exactly one surface. The root stays mounted so it can slide back, but
  // out of flow its box still counts toward the scroller's scrollable overflow — a root taller
  // than the view it opened puts a scrollbar on a view that fits. It keeps its layout while it
  // slides, which is exactly the window BodySwap has scrolling locked for.
  const hideRoot = isDrilledIn && isIdle

  return (
    <div ref={surfaceRef} className="relative">
      <motion.div
        aria-hidden={isDrilledIn || undefined}
        inert={isDrilledIn || undefined}
        className={
          isDrilledIn
            ? cx("pointer-events-none absolute inset-x-0 top-0", hideRoot && "hidden")
            : undefined
        }
        initial={false}
        animate={{ transform: `${translate}(${isDrilledIn ? -100 : 0}%)` }}
        transition={transition}
        onAnimationComplete={() => settle(activeKey)}
      >
        {root}
      </motion.div>

      <AnimatePresence initial={false} mode="popLayout">
        {activeKey === null ? null : (
          <motion.div
            key={activeKey}
            className="w-full"
            initial={{ transform: `${translate}(100%)` }}
            animate={{ transform: `${translate}(0%)` }}
            exit={{ transform: `${translate}(100%)` }}
            transition={transition}
            onAnimationComplete={() => settle(activeKey)}
          >
            <PresenceHidden>{children}</PresenceHidden>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Hides a surface from assistive tech while it animates away, so a screen reader never
 * finds two headers or two bodies mid-swap. (Focus has already been moved to the
 * incoming surface by then.)
 */
function PresenceHidden({ children }: { children: ReactNode }) {
  const isPresent = useIsPresent()
  return (
    <div
      aria-hidden={!isPresent || undefined}
      inert={!isPresent || undefined}
      className={isPresent ? undefined : "pointer-events-none"}
    >
      {children}
    </div>
  )
}

/**
 * The body's slide-over swap plus the height glide. The container's height is written
 * directly from the measured surface wrapper; during a swap the change runs as a native
 * WAAPI animation with the slides' duration and easing. Between the WAAPI slides and
 * the WAAPI height, no JavaScript runs per animation frame — the browser owns the whole
 * choreography, which keeps it smooth even when the main thread is busy.
 */
export function BodySwap({
  activeKey,
  root,
  children,
}: {
  activeKey: Key | null
  root: ReactNode
  children: ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const reduceMotionRef = useRef(reduceMotion)
  reduceMotionRef.current = reduceMotion
  const containerRef = useRef<HTMLDivElement | null>(null)
  const previousHeightRef = useRef<number | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const heightAnimationRef = useRef<Animation | null>(null)
  const heightFrameRef = useRef(0)

  // The swap window: height changes inside it glide; outside it (e.g. the accordion
  // sections animating within a panel) the height follows instantly, frame-by-frame.
  // A layout effect so the window is already open when ResizeObserver delivers the new
  // panel's measurement (plain effects can run after paint — too late).
  const [swapping, setSwapping] = useState(false)
  const swapUntilRef = useRef(0)
  const previousKeyRef = useRef<Key | null>(activeKey)
  useLayoutEffect(() => {
    const changed = previousKeyRef.current !== activeKey
    previousKeyRef.current = activeKey
    if (!changed || reduceMotion) {
      swapUntilRef.current = 0
      setSwapping(false)
      return
    }

    swapUntilRef.current = performance.now() + SWAP_DURATION_S * 1000
    setSwapping(true)
  }, [activeKey, reduceMotion])

  useLayoutEffect(() => {
    heightAnimationRef.current?.cancel()
    heightAnimationRef.current = null
  }, [activeKey])

  const finishSwap = useCallback((completedKey: Key | null) => {
    if (previousKeyRef.current !== completedKey) return
    swapUntilRef.current = 0
    heightAnimationRef.current?.cancel()
    heightAnimationRef.current = null
    setSwapping(false)
  }, [])

  const measureSurfaces = useCallback((el: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()
    resizeObserverRef.current = null
    cancelAnimationFrame(heightFrameRef.current)
    if (el) {
      const applyHeight = () => {
        const container = containerRef.current
        if (!container) return
        const next = el.offsetHeight
        const previous = previousHeightRef.current
        if (previous === null) {
          previousHeightRef.current = next
          return
        }
        if (
          previous !== next &&
          !reduceMotionRef.current &&
          performance.now() < swapUntilRef.current
        ) {
          heightAnimationRef.current?.cancel()
          heightAnimationRef.current = container.animate(
            [{ height: `${previous}px` }, { height: `${next}px` }],
            {
              duration: swapUntilRef.current - performance.now(),
              // ≈ the slides' cosine easeInAndOut (easeInOutSine).
              easing: "cubic-bezier(0.37, 0, 0.63, 1)",
            },
          )
        }
        // The final height is plain inline style (never set through React), which the
        // running animation overrides until it finishes.
        container.style.height = `${next}px`
        previousHeightRef.current = next
      }
      const observer = new ResizeObserver(() => {
        cancelAnimationFrame(heightFrameRef.current)
        // Call applyHeight asynchronously. RAC has its own ResizeObserver on the outer Popover;
        // if we resize synchronously, that fails to deliver, giving the warning
        // "ResizeObserver loop completed with undelivered notifications".
        heightFrameRef.current = requestAnimationFrame(applyHeight)
      })
      observer.observe(el)
      resizeObserverRef.current = observer
    }
  }, [])

  useLayoutEffect(
    () => () => {
      resizeObserverRef.current?.disconnect()
      cancelAnimationFrame(heightFrameRef.current)
      heightAnimationRef.current?.cancel()
    },
    [],
  )

  // Guard against stuck scroll: moving focus into a still-sliding panel can scroll the
  // container (overflow: hidden still scrolls programmatically), and the exiting
  // panel's removal can clamp scrollTop — both read as a settle-then-jump.
  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0
      containerRef.current.scrollTop = 0
    }
  }, [activeKey])

  return (
    <div
      ref={containerRef}
      data-test="drilldown body scroller"
      className={cx(
        "relative max-h-full min-h-0 gutter-scrollbar overflow-x-hidden overscroll-contain",
        // No scrolling while the height is mid-glide.
        swapping ? "overflow-y-hidden" : "overflow-y-auto",
      )}
    >
      <SlideSwap
        axis="x"
        activeKey={activeKey}
        root={root}
        surfaceRef={measureSurfaces}
        isIdle={!swapping}
        onAnimationComplete={finishSwap}
      >
        {children}
      </SlideSwap>
    </div>
  )
}

/**
 * Wraps a drilled-in view: moves focus into the view when it mounts (unless the view
 * autofocuses something itself, which wins because it runs later). Labelled by the view
 * title in the header surface, so receiving focus announces which view was entered.
 */
export function DrilldownPanel({
  labelledBy,
  children,
}: {
  labelledBy: string
  children: ReactNode
}) {
  const focusOnMount = useCallback((el: HTMLDivElement | null) => {
    if (el !== null && !el.contains(document.activeElement)) {
      // preventScroll: the panel is still animating in; scrolling it into view mid-swap
      // would drag the container's scroll position with it.
      el.focus({ preventScroll: true })
    }
  }, [])
  return (
    <div
      ref={focusOnMount}
      tabIndex={-1}
      role="group"
      aria-labelledby={labelledBy}
      className="outline-hidden"
    >
      {children}
    </div>
  )
}
